import React, { useState } from "react";
import { Link } from "react-router-dom";
import LegalPage, { Section, Well, H3, P, Warnung } from "./components/LegalPage";
import { brand as ds } from "./designSystem";
import { TRIAL_CANCEL_WARNING } from "./plans";

const BACKEND_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://schriftbot-backend.vercel.app";

/* § 312k BGB — Kündigungsbutton. Öffentlich erreichbar, ohne Login.
   Schritt 1: Bestätigungsseite mit den Vertragsdaten.
   Schritt 2: eindeutig beschrifteter Bestätigungsbutton "Jetzt kündigen".
   Gestaltung nach src/designSystem.js — dieselbe Hülle wie AGB, Widerruf
   und Impressum, damit die Kündigung so selbstverständlich aussieht wie
   der Kauf. */
/* Modul-Ebene, damit die Felder beim Tippen nicht neu eingehängt werden. */
const Field = ({ label, hint, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <span
      style={{
        fontFamily: ds.font.body,
        fontSize: 14,
        fontWeight: 600,
        color: ds.color.ink,
      }}
    >
      {label}
    </span>
    {hint && (
      <span
        style={{
          fontFamily: ds.font.body,
          fontSize: 14,
          color: ds.color.muted,
        }}
      >
        {hint}
      </span>
    )}
    {children}
  </label>
);

export default function Kuendigung() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleCancel = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/cancel-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, note }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Fehler ${res.status}`);
      setResult(data);
      setStatus("done");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return status === "done" ? (
    <LegalPage
      title="Kündigung bestätigt"
      intro="Wir haben deine Kündigung erhalten."
    >
      <Section>
        {result?.sofortBeendet ? (
          <P>
            Deine kostenlose Testphase ist damit beendet, ebenso der Zugang zu
            den Abo-Funktionen. Es wird nichts abgebucht.
          </P>
        ) : (
          <P>
            Dein Abo verlängert sich nicht weiter und endet{" "}
            {result?.endDate ? (
              <>
                am <strong>{fmtDate(result.endDate)}</strong>
              </>
            ) : (
              <>zum Ende des laufenden Abrechnungszeitraums</>
            )}
            . Bis dahin kannst du Schriftbot normal weiter nutzen.
          </P>
        )}
        <Well style={{ marginBottom: 16 }}>
          <P style={{ margin: 0 }}>
            Eingang: {fmtDate(result?.receivedAt) || "—"}
          </P>
        </Well>
        <P muted style={{ margin: 0 }}>
          Bitte bewahre oder drucke diese Bestätigung zu deinen Unterlagen aus.
          Eine zusätzliche Bestätigung erhältst du per E-Mail.
        </P>
        <div style={{ marginTop: 24 }}>
          <Link to="/" className="sb-btn sb-btn-primary">
            Zurück zur Startseite
          </Link>
        </div>
      </Section>
    </LegalPage>
  ) : (
    <LegalPage
      title="Abo kündigen"
      intro="Ohne Anmeldung, ohne Angabe von Gründen — zum Ende des laufenden Abrechnungsmonats."
    >
      <Section>
        <form onSubmit={handleCancel}>
          <P>
            Die Kündigung wird zum Ende des laufenden Abrechnungszeitraums
            wirksam; bis dahin bleibt dein Zugang bestehen. Gib bitte die
            E-Mail-Adresse an, mit der du dein Abo abgeschlossen hast.
          </P>

          <Warnung>{TRIAL_CANCEL_WARNING}</Warnung>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              marginTop: 24,
            }}
          >
            <Field label="E-Mail-Adresse deines Abos">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sb-input"
                placeholder="deine@email.de"
              />
            </Field>

            <Field label="Name" hint="Optional">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="sb-input"
              />
            </Field>

            <Field label="Anmerkung" hint="Optional">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="sb-input"
                style={{ resize: "vertical" }}
              />
            </Field>
          </div>

          {status === "error" && (
            <P style={{ color: ds.color.danger, marginTop: 16 }}>
              Das hat nicht geklappt: {error}. Du kannst auch formlos an
              schriftbot@gmail.com kündigen — die Frist wahrst du damit genauso.
            </P>
          )}

          <Warnung style={{ marginTop: 24, marginBottom: 0 }}>
            {TRIAL_CANCEL_WARNING}
          </Warnung>

          {/* § 312k Abs. 2 Nr. 2 BGB: Bestätigungsbutton, eindeutig
              beschriftet mit "Jetzt kündigen". */}
          <button
            type="submit"
            disabled={status === "loading"}
            className="sb-btn sb-btn-primary"
            style={{ width: "100%", marginTop: 24 }}
          >
            {status === "loading" ? "Wird verarbeitet …" : "Jetzt kündigen"}
          </button>
        </form>
      </Section>

      <Section>
        <H3>Lieber widerrufen?</H3>
        <P muted style={{ margin: 0 }}>
          Wenn dein Vertrag weniger als 14 Tage alt ist, kommt statt der
          Kündigung womöglich der{" "}
          <Link to="/widerruf" className="sb-link">
            Widerruf
          </Link>{" "}
          in Frage — der wirkt sofort und nicht erst zum Monatsende.
        </P>
      </Section>
    </LegalPage>
  );
}
